import { LitElement, html, css, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import '../job-chooser/job-chooser.js';
import { repeat } from 'lit/directives/repeat.js';
import { listenToUploadEventsForJob } from './PhotoUpload/PhotoUploadService.js';
import firebase from 'firebase/compat/app';
import '../../js/open-source/zipjs/zip.js';
import { DateTime } from 'luxon';
import { ref, createRef } from 'lit/directives/ref.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import { zipPendingJobData, clearPendingJobData } from '../../modules/ManagePendingJobData.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { WARN_INACTIVE_JOBS_AFTER_MS } from '../../workers/mobile/FirebaseWebWorker/MobileOfflineJobManager.js';
import { showToast } from '../../modules/ShowShoelaceToast.js';

/** @import { UploadProgress as PhotoUploadProgress } from './PhotoUpload/PhotoUploadService.js' */

const CLEAR_ALL_CHANGES_CONFIRMATION_TEXT = 'clear all changes';

/**
 * Returns a relative time string for a given timestamp.
 * @param {?number} timestamp
 * @param {'long' | 'short' | 'narrow'} style
 * @returns
 */
function getRelativeTimeString(timestamp, style = 'short') {
  if (!timestamp) return '';
  const relativeTime = DateTime.fromMillis(timestamp).toRelative({ style, unit: ['years', 'months', 'days', 'hours', 'minutes'] });
  if (!relativeTime) return '';
  if (relativeTime.startsWith('0')) return 'now';
  return relativeTime ?? '';
}
export class OfflineManagement extends LitElement {
  static jobCardStyles = css`
    .job-card {
      display: flex;
      flex-direction: column;
      padding: var(--sl-spacing-small);
      gap: var(--sl-spacing-small);
      background: var(--sl-color-neutral-0);
      border-radius: var(--sl-border-radius-x-large);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      box-shadow: var(--sl-shadow-x-small);
    }
    .job-card-header {
      display: flex;
      align-items: center;
    }
    .job-card-title {
      display: flex;
      font-size: var(--sl-font-size-large);
      font-weight: var(--sl-font-weight-bold);
      color: var(--sl-color-neutral-700);
      flex-grow: 1;
      gap: var(--sl-spacing-x-small);
    }
    .job-card-title[active] {
      color: var(--sl-color-primary);
    }
    .job-card-row {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }
    .job-card-row katapult-icon {
      flex-shrink: 0;
    }
    .job-card-task {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-x-small);
      background: var(--sl-color-neutral-50);
      padding: var(--sl-spacing-x-small);
      border-radius: var(--sl-border-radius-large);
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-600);
    }
    .job-card-task-title {
      margin-right: auto;
    }
    .job-card sl-progress-bar {
      --height: 10px;
      flex-grow: 1;
    }
    .job-card-sync-status {
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-semibold);
    }
    .upload-error-message {
      color: var(--sl-color-red-500);
      text-align: center;
    }
    .job-card-last-updated {
      align-self: flex-end;
      margin-left: auto;
      color: var(--sl-color-neutral-500);
    }
    .job-card-notification-dot {
      position: absolute;
      top: 0;
      right: 0;
      padding: 4px;
      background: var(--sl-color-danger);
      border-radius: var(--sl-border-radius-pill);
    }
  `;

  static styles = [
    this.jobCardStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: fixed;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background-color: var(--sl-color-neutral-100);
      }
      :host(:not([opened])) {
        display: none;
      }
      .header {
        display: flex;
        align-items: center;
        padding: var(--sl-spacing-small);
        position: relative;
        width: -webkit-fill-available;
      }
      .title {
        display: flex;
        align-items: center;
        height: 40px;
        font-size: var(--sl-font-size-large);
        font-weight: var(--sl-font-weight-semibold);
        color: var(--sl-color-neutral-800);
        gap: var(--sl-spacing-small);
        justify-content: flex-start;
        flex: 1;
      }
      .job-cards,
      .expiring-job-cards {
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-small);
      }
      .job-cards {
        min-height: 0;
        overflow-y: auto;
        padding: 0 var(--sl-spacing-small);
        padding-bottom: var(--sl-spacing-small);
      }
      .expiring-job-cards-container {
        --expiring-jobs-container-border-radius: 22px;
        --expiring-jobs-container-background: var(--sl-color-warning-200);
        --expiring-jobs-container-color: var(--sl-color-neutral-800);
      }
      .expiring-job-cards-header {
        display: flex;
        align-items: center;

        border-top-left-radius: var(--expiring-jobs-container-border-radius);
        border-top-right-radius: var(--expiring-jobs-container-border-radius);
        background: var(--expiring-jobs-container-background);
        padding-left: var(--sl-spacing-large);
      }
      .expiring-job-cards-header > katapult-icon {
        flex-shrink: 0;
      }
      .expiring-job-cards-header > sl-details {
        flex-grow: 1;
      }
      .expiring-job-cards-header > sl-details::part(base) {
        border-radius: 0;
        border: unset;
        background: unset;
      }
      .expiring-job-cards-header > sl-details::part(header) {
        color: var(--expiring-jobs-container-color);
        font-weight: var(--sl-font-weight-semibold);
        padding: var(--sl-spacing-small) var(--sl-spacing-medium);
      }
      .expiring-job-cards-header > sl-details::part(content) {
        padding-top: 0;
        font-size: var(--sl-font-size-small);
      }
      .expiring-job-cards {
        position: relative;
        border: 3px solid var(--expiring-jobs-container-background);
        border-top: unset;
        border-bottom-left-radius: var(--expiring-jobs-container-border-radius);
        border-bottom-right-radius: var(--expiring-jobs-container-border-radius);
        padding: var(--sl-spacing-x-small);
      }
      .expiring-job-cards .curve {
        position: absolute;
        height: calc(1.5 * var(--expiring-jobs-container-border-radius));
        width: calc(1.5 * var(--expiring-jobs-container-border-radius));
        overflow: hidden;
      }
      .expiring-job-cards .convex {
        height: inherit;
        width: inherit;
        position: absolute;
        border-radius: 50% 0 0 0;
        outline: var(--expiring-jobs-container-border-radius) solid var(--expiring-jobs-container-background);
      }

      #content {
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-small);
        max-width: 600px;
        width: 100%;
        min-height: 0;
      }
      #addJobRow {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: var(--sl-spacing-small);
        background: var(--sl-color-neutral-0);
        padding: var(--sl-spacing-2x-small);
        margin: 0 var(--sl-spacing-small);
        border-radius: var(--sl-border-radius-x-large);
        box-shadow: var(--sl-shadow-x-small);
      }
      #jobChooser {
        width: -webkit-fill-available;
        padding-left: var(--sl-spacing-small);
        background: none;
      }
      #offlineDetails::part(base) {
        border: none;
      }
      #offlineDetails::part(content) {
        padding-top: 0;
      }
      #offlineAlert {
        margin: 0 var(--sl-spacing-small);
      }
      #offlineAlert::part(message) {
        padding: 0;
      }
      sl-alert::part(base) {
        flex-shrink: 0;
      }
    `
  ];

  /**
   * @typedef {Object} OfflineJobInfo
   * @property {string} id - The id of the job.
   * @property {string} [name] - The name of the job.
   * @property {boolean} [readyForOfflineUse] - Indicates if the job is ready for offline use.
   * @property {number} [jobAddedAt] - The timestamp of when the job was added to offline storage.
   * @property {number} [jobLastActive] - The timestamp of when the job was last active.
   * @property {boolean} [jobDataHasServerChanges] - Indicates if the job data has changes on the server.
   * @property {number} [jobDataOutOfSyncAt] - The timestamp of when the job data fell out of sync with the server.
   * @property {Object} [jobDataCachingProgress] - The caching information for the job.
   * @property {PhotoUploadProgress} [photoUploadProgress] - The photo upload progress information.
   * @property {Object} [updateProgress] - The job data update progress information.
   */

  static properties = {
    opened: { type: Boolean, reflect: true },
    activeJobId: { type: String },
    userGroup: { type: String },
    userId: { type: String },
    isOnline: { type: Boolean },
    _jobs: { type: Object, state: true },
    _isSetUp: { type: Boolean, state: true },
    _cachingJob: { type: Boolean, state: true },
    _actionTarget: { type: Object, state: true },
    _clearUnsavedChangesConfirmationText: { type: String, state: true },
    _sendUnsavedChangesSuccess: { type: Boolean, state: true },
    _dumpToDeviceSuccess: { type: Boolean, state: true },
    _refreshingJob: { type: Boolean, state: true }
  };

  constructor() {
    super();
    /** @type {boolean} */
    this.opened = false;
    /** @type {string} */
    this.activeJobId = '';
    /** @type {string} */
    this.userGroup = '';
    /** @type {string} */
    this.userId = '';
    /** @type {boolean} */
    this.isOnline = false;
    /** @type {Record<string, OfflineJobInfo>} */
    this._jobs = {};
    /** @type {Record<string, { photoUploadEvents?: Function, updateProgress?: Function }>} */
    this._jobListenerDisconnects = {};
    /** @type {boolean} */
    this._isSetUp = false;
    /** @type {boolean} */
    this._cachingJob = false;
    /** @type {object} */
    this._actionTarget = null;
    /** @type {string} */
    this._clearUnsavedChangesConfirmationText = '';
    /** @type {boolean} */
    this._sendUnsavedChangesSuccess = false;
    /** @type {boolean} */
    this._dumpToDeviceSuccess = false;
    /** @type {Record<string, any>} */
    this._jobTapTimeouts = {};

    this._jobChooserRef = createRef();
    this._messageBannerRef = createRef();
    this._sendUnsavedChangesToSupportDialog = createRef();
    this._dumpJobToDeviceDialog = createRef();
    this._clearUnsavedChangesDialog = createRef();

    zip.workerScriptsPath = '../_resources/js/open-source/zipjs/';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tearDown();
  }

  render() {
    const getJobLastActive = (job) => (job.id == this.activeJobId ? Date.now() : (job?.jobLastActive ?? job?.jobAddedAt ?? 0));
    const jobs = Object.values(this._jobs).sort((a, b) => getJobLastActive(b) - getJobLastActive(a));
    const inactiveJobs = jobs.filter((job) => job.id !== this.activeJobId);
    const warnJobsExpiringAfterTime = Date.now() - WARN_INACTIVE_JOBS_AFTER_MS;
    const recentlyActiveJobs = jobs.filter((job) => getJobLastActive(job) > warnJobsExpiringAfterTime);
    const expiringJobs = jobs.filter((job) => getJobLastActive(job) <= warnJobsExpiringAfterTime);
    return html`
      <div class="header">
        <div class="title">
          <katapult-icon icon="cloud_sync"></katapult-icon>
          <div>Offline Management</div>
        </div>
        <katapult-button iconOnly noBorder icon="close" @click=${this.close} slot="trigger"></katapult-button>
      </div>
      <div id="content">
        ${when(
          !this._isSetUp,
          () => html`<sl-spinner style="font-size: 30px; align-self: center;"></sl-spinner>`,
          () => html`
            <div style="display: flex; align-items: center;">
              ${when(
                !this.isOnline,
                () => html`
                  <div style="flex-grow: 1;">
                    <sl-alert id="offlineAlert" variant="neutral" open>
                      <katapult-icon slot="icon" icon="cloud_off" outline></katapult-icon>
                      <sl-details id="offlineDetails">
                        <strong slot="summary">Reconnecting to Server</strong>
                        <katapult-icon slot="expand-icon" icon="help" outline></katapult-icon>
                        <katapult-icon slot="collapse-icon" icon="keyboard_arrow_up" style="transform: rotate(-90deg)"></katapult-icon>
                        <span>
                          Changes you make to Jobs are stored on this device. When your device reconnects, your changes will be synced
                          automatically and your Job will update to reflect recent changes from other users.</span
                        >
                      </sl-details>
                    </sl-alert>
                  </div>
                `,
                () => html`
                  <div id="addJobRow" style="flex-grow: 1;">
                    <div style="display: flex; flex: 1;">
                      <job-chooser
                        ${ref(this._jobChooserRef)}
                        id="jobChooser"
                        default-text="Select a Job to Use Offline"
                        no-create-new-job
                        .userGroup=${this.userGroup}
                        .offline=${!this.isOnline}
                        @selected-changed=${this.#selectedJobChangedHandler}
                        .disabled=${this._cachingJob || !this.isOnline}
                        style="${!this.isOnline ? 'pointer-events: none; opacity: 0.5;' : ''}"
                      ></job-chooser>
                    </div>
                    ${when(this._cachingJob, () => html`<sl-spinner style="font-size: 20px;"></sl-spinner>`)}
                  </div>
                `
              )}
              <sl-dropdown hoist>
                <katapult-button style="padding-left: 0; " slot="trigger" iconOnly noBorder icon="more_vert"></katapult-button>
                <sl-menu @sl-select=${(e) => this.#handleSelectedBulkAction(e)}>
                  <sl-menu-item
                    value="clear_all_offline_jobs"
                    ?disabled=${inactiveJobs.length === 0}
                    @click=${(event) =>
                      this.#showMessageBannerWhenDisabled(
                        event.currentTarget,
                        'There are no jobs to remove.  The active job cannot be removed.',
                        {
                          variant: 'warning'
                        }
                      )}
                  >
                    <katapult-icon slot="prefix" icon="playlist_remove"></katapult-icon>
                    <span>Remove all Jobs</span>
                  </sl-menu-item>
                </sl-menu>
              </sl-dropdown>
            </div>
            <div class="job-cards">
              ${repeat(
                recentlyActiveJobs,
                (job) => job.id,
                (job) => this.#renderJobCardTemplate(job)
              )}
              ${when(
                expiringJobs.length,
                () => html`
                  <div class="expiring-job-cards-container">
                    <div class="expiring-job-cards-header">
                      <katapult-icon icon="auto_delete" outline></katapult-icon>
                      <sl-details>
                        <div slot="summary" style="display: flex; align-items: center; gap: var(--sl-spacing-x-small);">
                          <span>Inactive Jobs</span>
                        </div>
                        <span>
                          The following jobs have not been opened in a while and will soon be cleared. You can open them now to prevent
                          automatic removal or add them again later.
                        </span>
                      </sl-details>
                    </div>
                    <div class="expiring-job-cards">
                      <div class="curve" style="top: 0; left: 0;"><div class="convex"></div></div>
                      <div class="curve" style="top: 0; right: 0; transform: scaleX(-1)"><div class="convex"></div></div>
                      ${repeat(
                        expiringJobs,
                        (job) => job.id,
                        (job) => this.#renderJobCardTemplate(job)
                      )}
                    </div>
                  </div>
                `
              )}
            </div>
          `
        )}
      </div>
      <katapult-dialog
        color="var(--sl-color-primary)"
        ${ref(this._dumpJobToDeviceDialog)}
        icon="mobile_arrow_down"
        title="Save Data to Device"
        modal
      >
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-small);">
          <span>You may save job data and photos to your device if an error is preventing them from being uploaded.</span>
          <span>Share the downloaded file with Katapult Pro Support for help uploading your data.</span>
        </div>
        <katapult-button slot="buttons" dialog-dismiss @click=${() => (this._actionTarget = null)}>Close</katapult-button>
        <div slot="buttons" style="display: flex; flex-direction: row; gap: var(--sl-spacing-small);">
          <katapult-button
            color="var(--sl-color-danger)"
            .disabled=${!this._dumpToDeviceSuccess}
            .callback=${() => {
              this._dumpJobToDeviceDialog.value?.close();
              this._clearUnsavedChangesDialog.value?.open();
            }}
            >Clear Data</katapult-button
          >
          <katapult-button
            .callback=${() => this.#downloadUnsavedChanges()}
            color="var(--sl-color-primary)"
            .disabled=${this._dumpToDeviceSuccess}
          >
            <div style="display: flex; flex-direction: row; gap: var(--sl-spacing-small);">
              ${when(
                this._dumpToDeviceSuccess,
                () => html`<katapult-icon icon="check_circle" size="small" color="var(--sl-color-success)"></katapult-icon>`
              )}
              <span>Save Data</span>
            </div>
          </katapult-button>
        </div>
      </katapult-dialog>
      <katapult-dialog
        ${ref(this._sendUnsavedChangesToSupportDialog)}
        title="Send Data to Support"
        icon="support_agent"
        color="var(--sl-color-primary)"
        modal
      >
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-small);">
          <span>You may send job data and photos to Katapult Pro Support if an error is preventing them from being uploaded.</span>
          <span>Katapult Pro Support can assist you with uploading your data.</span>
        </div>
        <katapult-button slot="buttons" dialog-dismiss @click=${() => (this._actionTarget = null)}>Close</katapult-button>
        <div slot="buttons" style="display: flex; flex-direction: row; gap: var(--sl-spacing-small);">
          <katapult-button
            color="var(--sl-color-danger)"
            .disabled=${!this._sendUnsavedChangesSuccess}
            .callback=${() => {
              this._sendUnsavedChangesToSupportDialog.value?.close();
              this._clearUnsavedChangesDialog.value?.open();
            }}
            >Clear Data</katapult-button
          >
          <katapult-button
            .callback=${() => this.#sendUnsavedChangesToSupport()}
            color="var(--sl-color-primary)"
            .disabled=${this._sendUnsavedChangesSuccess}
          >
            <div style="display: flex; flex-direction: row; gap: var(--sl-spacing-small);">
              ${when(
                this._sendUnsavedChangesSuccess,
                () => html`<katapult-icon icon="check_circle" size="small" color="var(--sl-color-success)"></katapult-icon>`
              )}
              <span>Send Data</span>
            </div>
          </katapult-button>
        </div>
      </katapult-dialog>
      <katapult-dialog
        color="var(--sl-color-danger)"
        ${ref(this._clearUnsavedChangesDialog)}
        icon="delete_forever"
        title="Clear Job Data"
        modal
      >
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-small);">
          <span>You are about to clear all job data and photos that have not been uploaded!</span>
          <span
            >This should only be done if you are encountering an upload error and have already saved your data to your device or sent your
            data to Katapult Pro Support.</span
          >
          <sl-input
            .value=${this._clearUnsavedChangesConfirmationText}
            placeholder="Type 'clear all changes' to continue"
            @sl-change=${(e) => (this._clearUnsavedChangesConfirmationText = e?.target?.value ?? '')}
          ></sl-input>
          <sl-alert variant="danger" open>
            <span>Clearing unsaved changes cannot be undone, proceed with caution!</span>
            <katapult-icon slot="icon" icon="info" color="var(--sl-color-danger)" size="small"></katapult-icon>
          </sl-alert>
        </div>
        <katapult-button slot="buttons" dialog-dismiss @click=${() => (this._actionTarget = null)}>Close</katapult-button>
        <katapult-button
          .disabled=${this._clearUnsavedChangesConfirmationText.trim().toLowerCase() !== CLEAR_ALL_CHANGES_CONFIRMATION_TEXT}
          .callback=${() => this.#evaluateClearAllChanges()}
          color="var(--sl-color-danger)"
          slot="buttons"
          >Clear All Changes</katapult-button
        >
      </katapult-dialog>
    `;
  }

  /**
   *
   * @param {OfflineJobInfo} jobInfo
   * @returns
   */
  #renderJobCardTemplate(jobInfo) {
    const isActiveJob = jobInfo.id === this.activeJobId;
    const { updateProgress, photoUploadProgress } = jobInfo;

    const isUploadingJobDataChanges = updateProgress != null && (updateProgress.complete !== true || updateProgress.failed === true);
    const isUploadingJobPhotos =
      photoUploadProgress != null && (photoUploadProgress.complete !== true || photoUploadProgress.failed === true);
    const isFailingToSync = updateProgress?.failed || photoUploadProgress?.failed;

    const isDownloadingJobFromServer = jobInfo.jobDataCachingProgress != null && jobInfo.jobDataCachingProgress.complete !== true;
    const jobUpdatesAvailableOnServer = !isActiveJob && jobInfo.jobDataHasServerChanges;

    const isUploadingJob = isUploadingJobDataChanges || isUploadingJobPhotos;
    const jobIsUpToDate = this.isOnline && !jobUpdatesAvailableOnServer && !isUploadingJob;
    const isDownloadingJob = isDownloadingJobFromServer;

    const timestamps = [];
    if (jobInfo?.jobDataOutOfSyncAt) timestamps.push(jobInfo.jobDataOutOfSyncAt);
    if (updateProgress?.incompleteAt) timestamps.push(updateProgress.incompleteAt);
    if (photoUploadProgress?.incompleteAt) timestamps.push(photoUploadProgress.incompleteAt);
    const oldestTimestamp = timestamps.length ? Math.min(...timestamps) : null;

    const lastSyncedTime = oldestTimestamp;
    const lastSyncedTimeString = getRelativeTimeString(lastSyncedTime);

    return html`
      <div class="job-card" @click=${() => this.#handleJobTapped(jobInfo.id)}>
        <div class="job-card-header">
          <div class="job-card-title" ?active=${isActiveJob}>
            <span> ${jobInfo.name ?? 'Unknown Job'} </span>
            ${when(isActiveJob, () => html`<sl-tag size="small" pill>Active Job</sl-tag>`)}
            <sl-dropdown style="margin-left: auto;" @click=${(event) => event.stopPropagation()}>
              <div style="position: relative;" slot="trigger">
                <katapult-icon
                  style="padding: 0;"
                  color="${isFailingToSync ? 'var(--sl-color-neutral-100)' : 'var(--sl-color-neutral-700)'}"
                  icon="more_vert"
                ></katapult-icon>
                ${when(isFailingToSync, () => html` <div class="job-card-notification-dot"></div> `)}
              </div>
              <sl-menu @sl-select=${(e) => this.#handleSelectedJobAction(e, jobInfo)}>
                <sl-menu-label>Actions</sl-menu-label>
                <sl-menu-item
                  value="openJob"
                  data-job-id=${jobInfo.id}
                  ?disabled=${isActiveJob}
                  @click=${(event) => this.#showMessageBannerWhenDisabled(event.currentTarget, 'This job is already active')}
                >
                  <katapult-icon slot="prefix" icon="open_in_browser"></katapult-icon>
                  <span>Open Job</span>
                </sl-menu-item>
                <sl-menu-item
                  value="removeJob"
                  data-job-id=${jobInfo.id}
                  ?disabled=${isActiveJob}
                  @click=${(event) =>
                    this.#showMessageBannerWhenDisabled(event.currentTarget, 'You cannot remove the active job', { variant: 'warning' })}
                >
                  <katapult-icon slot="prefix" icon="close"></katapult-icon>
                  <span>Remove Job</span>
                </sl-menu-item>
                <sl-divider></sl-divider>
                <sl-menu-label>Troubleshooting</sl-menu-label>
                <sl-menu-item value="dumpJobToDevice">
                  <katapult-icon slot="prefix" icon="mobile_arrow_down"></katapult-icon>
                  Save Data to Device
                </sl-menu-item>
                <sl-menu-item
                  value="sendUnsavedChangesToSupport"
                  ?disabled=${!this.isOnline}
                  @click=${(event) =>
                    this.#showMessageBannerWhenDisabled(event.currentTarget, 'Data cannot be sent to support while offline', {
                      variant: 'warning'
                    })}
                >
                  <katapult-icon slot="prefix" icon="support_agent"></katapult-icon>
                  Send Data to Support
                </sl-menu-item>
              </sl-menu>
            </sl-dropdown>
          </div>
        </div>
        <div class="job-card-row">
          ${when(
            isUploadingJob,
            () =>
              when(
                this.isOnline,
                () => html`
                  <katapult-icon color="var(--sl-color-neutral)" icon="cloud_upload" size="22" outline></katapult-icon>
                  <div class="job-card-sync-status">Uploading...</div>
                `,
                () => html`
                  <katapult-icon color="var(--sl-color-neutral)" icon="cloud_off" size="22" outline></katapult-icon>
                  <div class="job-card-sync-status">Waiting to upload</div>
                `
              ),
            () =>
              when(
                jobUpdatesAvailableOnServer || isDownloadingJob,
                () => html`
                  <katapult-button
                    style="font-weight: var(--sl-font-weight-semibold);"
                    icon="cloud"
                    noOutline
                    color="var(--sl-color-neutral-200)"
                    textColor="var(--sl-color-neutral-600)"
                    .disabled=${!this.isOnline}
                    .loading=${isDownloadingJob}
                    @click=${(event) => {
                      event.stopPropagation();
                      this.downloadAvailableJobUpdates(jobInfo.id);
                    }}
                  >
                    ${isDownloadingJob ? 'Downloading...' : 'Download Updates'}
                  </katapult-button>
                `,
                () =>
                  when(
                    this.isOnline,
                    () => html`
                      <katapult-icon color="var(--sl-color-success)" icon="check_circle" size="22"></katapult-icon>
                      <div class="job-card-sync-status">Up to date</div>
                    `,
                    () => html`
                      <katapult-icon color="var(--sl-color-neutral)" icon="cloud_off" size="22" outline></katapult-icon>
                      <div class="job-card-sync-status">Reconnecting to server</div>
                    `
                  )
              )
          )}
          ${when(
            !jobIsUpToDate && lastSyncedTime != null && lastSyncedTimeString !== '',
            () => html`<div class="job-card-last-updated">last up to date ${lastSyncedTimeString}</div>`
          )}
        </div>
        <!-- Saving Job Changes -->
        ${when(
          isUploadingJobDataChanges,
          () => html`
            <div class="job-card-task">
              <div class="job-card-row">
                <span class="job-card-task-title">Job Changes</span>
                <div>
                  <span>${updateProgress?.numUpdatesComplete ?? 0} saved • ${updateProgress?.numUpdatesRemaining ?? 0} remaining</span>
                  ${when(
                    updateProgress?.failed,
                    () => html`<span style="color: var(--sl-color-danger);"> • ${updateProgress?.numUpdatesFailed ?? 0} failed</span>`
                  )}
                </div>
              </div>
              <sl-progress-bar value=${updateProgress?.progress ?? 0}></sl-progress-bar>
              ${when(
                updateProgress?.failed,
                () =>
                  html` <div class="job-card-row" style="color: var(--sl-color-danger)">
                    We are having trouble saving some of your changes. Please contact support for assistance
                  </div>`
              )}
            </div>
          `
        )}
        <!-- Uploading Photos -->
        ${when(
          isUploadingJobPhotos,
          () => html`
            <div class="job-card-task">
              <div class="job-card-row">
                <span class="job-card-task-title">Photos</span>
                <div>
                  <span
                    >${photoUploadProgress?.numPhotosUploaded ?? 0} uploaded • ${photoUploadProgress?.numPhotosRemaining ?? 0}
                    remaining</span
                  >
                  ${when(
                    photoUploadProgress?.failed,
                    () => html`<span style="color: var(--sl-color-danger);"> • ${photoUploadProgress?.numPhotosFailed ?? 0} failed</span>`
                  )}
                </div>
              </div>
              <sl-progress-bar value=${photoUploadProgress?.progress ?? 0}></sl-progress-bar>
              ${when(
                photoUploadProgress?.failed,
                () =>
                  html` <div class="job-card-row" style="color: var(--sl-color-danger)">
                    We are having trouble uploading some of your photos. Please contact support for assistance
                  </div>`
              )}
            </div>
          `
        )}
      </div>
    `;
  }

  async #buildOfflineJobsFromDirectory(offlineJobs) {
    // Delete jobs that aren't included in the offline jobs list anymore
    const jobsToDelete = Object.keys(this._jobs).filter((jobId) => !offlineJobs.some((offlineJob) => offlineJob.jobId === jobId));
    jobsToDelete.forEach((jobId) => {
      this.#removePhotoListenerForJob(jobId);
      this.#removeUploadProgressListenerForJob(jobId);
      delete this._jobs[jobId];
    });

    // Sync the view with the offline jobs to ensure all jobs in local storage are shown
    for (const offlineJobData of offlineJobs) {
      // Update the job properties with the latest data from local storage
      const { jobId, name, addedAt, outOfSyncAt, lastActiveAt, cachingProgress, hasServerChanges } = offlineJobData;
      this.#addOrUpdateJobProperties(jobId, {
        name,
        jobAddedAt: addedAt,
        jobDataOutOfSyncAt: outOfSyncAt,
        jobLastActive: lastActiveAt,
        jobDataCachingProgress: cachingProgress,
        jobDataHasServerChanges: hasServerChanges
      });

      // Get the job's current listeners to determine if new listeners need to be added
      const jobListeners = this._jobListenerDisconnects[jobId] ?? {};

      // Set up a progress listener for any offline job that doesn't already have one to ensure the view is updated with the latest progress information
      const jobHasProgressListener = jobListeners.updateProgress != null;
      if (!jobHasProgressListener) this.#addUploadProgressListenerForJob(jobId);

      // If a job has photo uploads, set up a photo upload listener
      const jobAlreadyHasPhotoListener = jobListeners.photoUploadEvents != null;
      if (!jobAlreadyHasPhotoListener) this.#addPhotoListenerForJob(jobId);
    }

    // Ensure that the view is always forced to update
    this.requestUpdate();
  }

  /**
   * Tears down the offline management by:
   * Removing photo upload callbacks.
   * Removing job data upload callbacks.
   */
  #tearDown() {
    this._disconnectOfflineJobsListener?.();
    const jobsWithPhotoListeners = Object.keys(this._jobListenerDisconnects);
    jobsWithPhotoListeners.forEach((jobId) => {
      this.#removeUploadProgressListenerForJob(jobId);
      this.#removePhotoListenerForJob(jobId);
    });
    this._isSetUp = false;

    if (this._updateEveryMinuteInterval != null) {
      clearInterval(this._updateEveryMinuteInterval);
      this._updateEveryMinuteInterval = null;
    }
  }

  /**
   * Sets up the offline management by:
   * Loading cached jobs from local storage.
   * Attaching photo upload callbacks.
   * Attaching job data upload callbacks.
   */
  async #setUp() {
    this.#tearDown();

    this._updateEveryMinuteInterval = setInterval(() => this.requestUpdate(), 60_000);

    this._disconnectOfflineJobsListener = globalThis.FirebaseWorker.listenToOfflineJobs((offlineJobs) => {
      // Set up all the offline jobs
      this.#buildOfflineJobsFromDirectory(offlineJobs);
    });

    // Setup photo upload listeners on the current job.
    if (this.activeJobId) this.#addPhotoListenerForJob(this.activeJobId);

    this._isSetUp = true;
  }

  open() {
    this.#setUp();
    this.opened = true;
  }

  close() {
    this.opened = false;
  }

  #addOrUpdateJobProperties(jobId, properties) {
    this._jobs[jobId] ??= { id: jobId };
    Object.assign(this._jobs[jobId], properties);
    this.requestUpdate();
  }

  #addPhotoListenerForJob(jobId) {
    this.#removePhotoListenerForJob(jobId);

    const disconnect = listenToUploadEventsForJob(jobId, (photoUploadProgress) => {
      this.#addOrUpdateJobProperties(jobId, { photoUploadProgress });
    });

    const disconnectAndRemoveProgress = () => {
      disconnect();
      this.#addOrUpdateJobProperties(jobId, { photoUploadProgress: null });
    };

    this._jobListenerDisconnects[jobId] ??= {};
    this._jobListenerDisconnects[jobId].photoUploadEvents = disconnectAndRemoveProgress;
  }

  #removePhotoListenerForJob(jobId) {
    const disconnect = this._jobListenerDisconnects[jobId]?.photoUploadEvents;
    if (disconnect) {
      disconnect();
      delete this._jobListenerDisconnects[jobId].photoUploadEvents;
    }
  }

  #addUploadProgressListenerForJob(jobId) {
    this.#removeUploadProgressListenerForJob(jobId);

    const disconnect = globalThis.FirebaseWorker.listenToUpdateProgressForJob(jobId, (updateProgress) => {
      this.#addOrUpdateJobProperties(jobId, { updateProgress });
    });

    const disconnectAndRemoveProgress = () => {
      disconnect();
      this.#addOrUpdateJobProperties(jobId, { updateProgress: null });
    };

    this._jobListenerDisconnects[jobId] ??= {};
    this._jobListenerDisconnects[jobId].updateProgress = disconnectAndRemoveProgress;
  }

  #removeUploadProgressListenerForJob(jobId) {
    const disconnect = this._jobListenerDisconnects[jobId]?.updateProgress;
    if (disconnect) {
      disconnect();
      delete this._jobListenerDisconnects[jobId].updateProgress;
    }
  }

  setActiveJob(jobId) {
    // If the job is being cleared, remove listeners from the currently active job
    if (!jobId) {
      this.#removePhotoListenerForJob(this.activeJobId);
      this.#removeUploadProgressListenerForJob(this.activeJobId);
    }
    // Set up listeners for the new active job
    else {
      this.#addPhotoListenerForJob(jobId);
      this.#addUploadProgressListenerForJob(jobId);
    }
    this.activeJobId = jobId;
  }

  async #selectedJobChangedHandler(event) {
    // If there is no selected job or a job is currently being cached, do nothing
    const jobIdToCache = event.detail.value;
    if (!jobIdToCache || this._cachingJob) return;

    // Set a flag to indicate a job is being cached
    this._cachingJob = true;

    // Clear the selected job from the job chooser
    const jobChooser = this._jobChooserRef.value;
    if (jobChooser) jobChooser.selected = '';

    // Set up the offline job with FirebaseWebWorker
    const { err: couldNotSetUpOfflineJob } = await globalThis.FirebaseWorker.setupOfflineJob(jobIdToCache);

    // Clear the caching flag after the caching is complete
    this._cachingJob = false;

    // Show an error message if the offline job could not be set up
    if (couldNotSetUpOfflineJob) {
      this.#showMessageBanner('There was an error with downloading your job for offline use. Please try again or contact Support.', {
        variant: 'danger'
      });
      return;
    }
  }

  /**
   * @typedef {object} MessageBannerOptions
   * @property {'primary' | 'success' | 'neutral' | 'danger' | 'warning'} [variant='primary'] - The variant of the banner which determines its color
   * @property {string} [icon] - The icon to display in the banner
   */

  /**
   * Shows a message in a banner at the bottom of the screen
   * @param {string} message - The message to show in the banner
   * @param {MessageBannerOptions} [options] - Additional options for the banner
   */
  #showMessageBanner(message, options = {}) {
    const DEFAULT_ICONS_BY_VARIANT = {
      primary: 'info',
      success: 'check_circle',
      neutral: 'info',
      danger: 'error',
      warning: 'warning'
    };
    const { variant = 'primary', icon = DEFAULT_ICONS_BY_VARIANT[variant] } = options;
    showToast(
      {
        variant,
        content: html`
          <katapult-icon slot="icon" icon="${icon}"></katapult-icon>
          <span>${message}</span>
        `
      },
      'bottom'
    );
  }

  /**
   *
   * @param {Element} elem
   * @param {string} message - The message to show in the banner
   * @param {MessageBannerOptions} [options] - Additional options for the banner
   */
  #showMessageBannerWhenDisabled(elem, message, options) {
    if (!elem?.disabled && !elem?.hasAttribute('disabled')) return;
    this.#showMessageBanner(message, options);
  }

  #openJob(jobId) {
    this.dispatchEvent(new CustomEvent('open-job', { detail: { jobId } }));
    this.close();
  }

  async #handleSelectedJobAction(event, jobInfo) {
    if (!jobInfo) return;
    // Set the action target
    this._actionTarget = jobInfo;

    // Get the action and perform the corresponding task
    const action = event.detail.item.value;
    switch (action) {
      case 'openJob': {
        const jobId = event.detail.item.dataset.jobId;
        if (jobId) {
          this.#openJob(jobId);
        }
        break;
      }
      case 'removeJob': {
        const jobId = event.detail.item.dataset.jobId;
        if (jobId) {
          globalThis.FirebaseWorker.clearOfflineJob(jobId);
        }
        break;
      }
      case 'sendUnsavedChangesToSupport': {
        this._sendUnsavedChangesSuccess = false;
        this._sendUnsavedChangesToSupportDialog.value?.open();
        break;
      }
      case 'dumpJobToDevice': {
        this._dumpToDeviceSuccess = false;
        this._dumpJobToDeviceDialog.value?.open();
        break;
      }
    }
  }

  async downloadAvailableJobUpdates(jobId) {
    await globalThis.FirebaseWorker.recacheOfflineJob(jobId);
  }

  #handleSelectedBulkAction(event) {
    const action = event.detail.item.value;
    switch (action) {
      case 'clear_all_offline_jobs': {
        this.#removeAllOfflineJobs();
        break;
      }
    }
  }

  async #sendUnsavedChangesToSupport() {
    try {
      // If there is no action target, do nothing
      if (!this._actionTarget || !this.userId || !this.userGroup) {
        throw new Error('Data is missing to send unsaved changes');
      }

      // Get the zip as blob of all the necessary files
      const { id: jobId } = this._actionTarget;
      const zipAsBlob = await zipPendingJobData(jobId);

      // Upload this zip to Google Cloud storage
      const storagePath = `mobile_offline_backup/${this.userGroup || 'no_user_group'}/${this.userId}/${jobId}`;
      const fileName = `${new Date().toISOString()}.zip`;
      const storageRef = firebase.storage().ref(storagePath).child(fileName);
      await storageRef.put(zipAsBlob);

      // Send an email to the mobile backup email
      await firebase.functions().httpsCallable('email')({
        template: 'mobileOfflineDataBackup',
        userGroup: this.userGroup || 'no_user_group',
        storagePath: `${storagePath}/${fileName}`
      });
      // Notify success
      this._sendUnsavedChangesSuccess = true;
    } catch (error) {
      this.#showMessageBanner('There was an error in sending your unsaved changes.  You can try again or contact Support.', {
        variant: 'danger'
      });
      this._actionTarget = null;
      this._sendUnsavedChangesToSupportDialog.value?.close();
    }
  }

  async #evaluateClearAllChanges() {
    try {
      const { id: jobId } = this._actionTarget;
      // Check to see if the user correctly typed the confirmation text
      const canClearAllChanges = this._clearUnsavedChangesConfirmationText.trim().toLowerCase() === CLEAR_ALL_CHANGES_CONFIRMATION_TEXT;
      if (canClearAllChanges) {
        await clearPendingJobData(jobId);
        // Show a success message
        this.#showMessageBanner('All changes were cleared successfully', { variant: 'success' });
      }
    } catch (error) {
      this.#showMessageBanner('There was an error clearing your unsaved changes. Please try again or contact Support.', {
        variant: 'danger'
      });
    }

    // Close the dialog when the action is complete
    this._clearUnsavedChangesDialog.value?.close();

    // Clear the action target and confirmation input
    this._actionTarget = null;
    this._clearUnsavedChangesConfirmationText = '';
  }

  async #downloadUnsavedChanges() {
    // If there is no action target, do nothing
    if (!this._actionTarget) {
      // Clear the action target
      this._actionTarget = null;
      return;
    }

    try {
      // Zip the pending job data and download it
      const { id: jobId, name: jobName } = this._actionTarget;
      const zipAsBlob = await zipPendingJobData(jobId);
      const downloadURL = URL.createObjectURL(zipAsBlob);
      const downloadElement = document.createElement('a');
      downloadElement.href = downloadURL;
      downloadElement.download = `${jobName}_unsaved_changes.zip`;
      downloadElement.click();
      // Clean up the URL object
      URL.revokeObjectURL(downloadURL);

      // Notify success
      this._dumpToDeviceSuccess = true;
    } catch (error) {
      this.#showMessageBanner('There was an error downloading your unsaved changes. Please try again or contact Support.', {
        variant: 'danger'
      });
      // Close the dialog and clear the action target if there's an error
      this._actionTarget = null;
      this._dumpJobToDeviceDialog.value?.close();
    }
  }

  async #removeAllOfflineJobs() {
    // Show a confirmation dialog to ensure the user wants to remove all offline jobs since this action cannot be undone
    const confirmDialog = KatapultDialog.confirm({
      text: `This will remove all offline jobs from this list, including jobs that are open in other tabs.\n\nYour active job and jobs with unsaved changes will not be removed.\n\nAre you sure you want to continue?`,
      dialog: {
        title: 'Remove All Jobs',
        color: 'var(--sl-color-primary)',
        icon: 'playlist_remove'
      }
    });
    const userConfirmed = await confirmDialog.confirmed;
    // Only run the action if the user confirmed that they want to remove all offline jobs
    if (!userConfirmed) return;

    // Clear all offline jobs and show a success banner when it's finished
    await globalThis.FirebaseWorker.clearAllOfflineJobs();
    this.#showMessageBanner('All jobs were removed successfully', { variant: 'success' });
  }

  /**
   * Handles when a job card is tapped by requiring a double tap to open the job.
   * @param {string} jobId - The id of the tapped job
   */
  #handleJobTapped(jobId) {
    // If the tapped job is already active, do nothing.
    if (jobId === this.activeJobId) return;

    const existingTimeout = this._jobTapTimeouts[jobId];

    // On the first tap (as indicated by the absence of a timeout), set a timeout instructing the
    // user to double tap to open the job and return early.  If the user fails to tap again
    // before the timeout finishes, the message will be shown and the timeout will be cleared.
    if (existingTimeout == null) {
      this._jobTapTimeouts[jobId] = setTimeout(() => {
        this.#showMessageBanner('Double-tap to open a job', { variant: 'primary', icon: 'touch_double' });
        delete this._jobTapTimeouts[jobId];
      }, 250);
      return;
    }

    // If the user taps again before the timeout finishes, clear the timeout and open the job.
    clearTimeout(existingTimeout);
    delete this._jobTapTimeouts[jobId];
    this.#openJob(jobId);
  }
}

customElements.define('offline-management', OfflineManagement);
